import { AppBar, Card, CardContent, FormControlLabel, FormGroup, FormLabel, Switch, TextField, Toolbar } from "@mui/material";
import Grid from "@mui/material/Grid";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { FormStyleClasses, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonCancel from "components/Buttons/ButtonCancel";
import ButtonSave from "components/Buttons/ButtonSave";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import FormSubtitle from "components/FormSubtitle";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import QuillTextBox from "components/Quill/QuillTextBox";
import { RecordAbout } from "components/RecordAbout";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import LibUtils from "lib/LibUtils";
import { GetSetupUserDto, SaveSetupUserDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { Prompt } from "react-router";
import BlockCodeEditDialog from "views/System/Components/BlockCodeEditDialog";
import LocationEditDialog from "views/System/Components/LocationEditDialog";
import { ISetupProps } from "views/System/Components/SetupAdminEdit";
import SetupUsersLogDialog from "views/System/Components/SetupUsersLogDialog";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
}

interface IState {
    dialog: any,
    formCycle: RequestLifeCycle,
    lastError: any,
    showWelcomeEditor: boolean,
    record: Partial<GetSetupUserDto>,
    validate: {
        localName: boolean,
    };
}

type Props = ISetupProps & IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class SetupUsersEdit extends React.Component<Props, IState> {

    private compareDto: any = null;

    ///
    constructor(props: Props) {
        super(props);

        this.compareDto = null;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            lastError: null,
            dialog: null,
            record: null,
            showWelcomeEditor: false,
            validate: {
                localName: null,
            },
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.System.Components.SetupUsersEdit." + r, f);
    }

    ///
    private load() {
        api.getSetupUser()
            .then((dto) => {
                this.compareDto = LibUtils.deepCopy(dto);

                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    record: dto,
                    showWelcomeEditor: dto.userWelcomeText != null,
                    validate: {
                        localName: true,
                    }
                }, () => this.props.onEditing(false));
            })
            .catch((error: any) => {
                this.setState({
                    formCycle: RequestLifeCycle.InitFailed,
                    lastError: error
                });
            });
    }

    ///
    private getPostDto(): SaveSetupUserDto {
        const { record } = this.state;

        return {
            localName: record.localName,
            chooseLocalFromList: record.chooseLocalFromList,
            editLocalInUserProfile: record.editLocalInUserProfile,
            userWelcomeText: this.state.showWelcomeEditor ? record.userWelcomeText : null
        };
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        this.setState(state => {
            switch (name) {
                case "localName":
                    state.record.localName = v;
                    state.validate.localName = v != "";
                    break;
                case "userWelcomeText":
                    state.record.userWelcomeText = v;
                    break;
                default:
                    state.record[name] = v;
                    break;
            }
            return state;
        }, () => this.props.onEditing(this.isRecordChanged()));
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (!this.formIsValid()) return;

        const dto = this.getPostDto();

        this.setState({
            formCycle: RequestLifeCycle.Posting,
        });

        api.saveSetupUser(dto)
            .then((saved) => {
                this.compareDto = LibUtils.deepCopy(this.state.record);
                this.props.snackbar.showMessage(this.props.t("Common.Literals.UpdateSuccess"), "success");
                this.setState({ formCycle: RequestLifeCycle.PostSuccess }, () => this.props.onEditing(false));
            })
            .catch((err: any) => {
                this.setState({ lastError: err, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private isRecordChanged() {
        return this.state.lastError == null && !LibUtils.deepCompare(this.state.record, this.compareDto);
    }

    ///
    private formIsValid() {
        return LibUtils.isAllPropertiesWithValue(this.state.validate, true);
    }

    ///
    private handleEditLocal = () => {
        this.setState({
            dialog: (
                <LocationEditDialog
                    onClose={
                        (total) => {
                            this.setState(state => {
                                state.record.counters.listLocalCounter = total;
                                return { ...state, dialog: null };
                            });
                        }
                    }
                />
            )
        });
    };

    ///
    private handleEditBlock = () => {
        this.setState({
            dialog: (
                <BlockCodeEditDialog
                    onClose={
                        (total) => {
                            this.setState(state => {
                                state.record.counters.blockCodeCounter = total;
                                return { ...state, dialog: null };
                            });
                        }
                    }
                />
            )
        });
    };

    ///
    private renderToolbar() {
        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar} >
                <Toolbar>
                    <div className={GlobalClassKeys.pushRight}>
                        {
                            this.isRecordChanged() &&
                            <ButtonCancel
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.load()}
                            />
                        }

                        <ButtonSave
                            disabled={!this.formIsValid()}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                        />
                    </div>
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <RecordAbout
                        record={this.state.record.recordInfo}
                        hideCreator={true}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <SetupUsersLogDialog
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private renderFormMain() {
        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("UserRecordTitle")} />

                    <CardContent>

                        <MuiContainer margin="normal">

                            <FormSubtitle text="Local" />

                            <TextField
                                name="localName"
                                label={this.res("LocalNameLabel")}
                                error={validate.localName === false}
                                margin="dense"
                                value={record.localName || ""}
                                onChange={this.handleChange}
                                inputProps={{ maxLength: 255 }}
                                fullWidth
                                required
                            />
                            <br />
                            <br />

                            <MuiContainer margin="normal">
                                <Grid container justifyContent="flex-start" alignItems="flex-start">
                                    <Grid item>
                                        <Switch
                                            name="chooseLocalFromList"
                                            checked={record.chooseLocalFromList}
                                            onChange={this.handleChange}
                                            value="chooseLocalFromList"
                                            color="secondary"
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <div>
                                            <FormLabel>{this.res("LocalFromListLabel")}</FormLabel>
                                        </div>
                                        <div>
                                            <MuiLink color="secondary" onClick={this.handleEditLocal}>{this.res("EditLocalListLabel")}...</MuiLink>
                                        </div>
                                    </Grid>
                                </Grid>
                            </MuiContainer>

                            <MuiContainer margin="normal">
                                <Grid container justifyContent="flex-start" alignItems="center">
                                    <Grid item>
                                        <Switch
                                            name="editLocalInUserProfile"
                                            checked={record.editLocalInUserProfile}
                                            onChange={this.handleChange}
                                            value="editLocalInUserProfile"
                                            color="secondary"
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <FormLabel>{this.res("LocalProfileEditLabel")}</FormLabel>
                                    </Grid>
                                </Grid>
                            </MuiContainer>

                        </MuiContainer>


                        <MuiContainer margin="normal">

                            <FormSubtitle text={this.res("BlockCodeSubtitle")} />

                            <div>
                                <MuiLink color="secondary" onClick={this.handleEditBlock}>{this.res("EditBlockCodeLabel")}...</MuiLink>
                            </div>
                            <div>
                                <MuiMuted>{this.res("BlockCodeText", { count: record.counters.blockCodeCounter })}</MuiMuted>
                            </div>
                        </MuiContainer>


                        <MuiContainer margin="normal">

                            <FormSubtitle text={this.res("WelcomeSubtitle")} />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={this.state.showWelcomeEditor}
                                        onChange={() => this.setState({ showWelcomeEditor: !this.state.showWelcomeEditor })}
                                        value=""
                                        color="secondary"
                                        size="small"
                                    />
                                }
                                label={this.res("SendWelcomeLabel")}
                            />

                            {
                                this.state.showWelcomeEditor &&
                                <FormGroup>
                                    <QuillTextBox
                                        toolbar={true}
                                        height={"20em"}
                                        content={record.userWelcomeText}
                                        onChange={(content) => this.updateRecord("userWelcomeText", content)}
                                        placeholder={this.res("WelcomePlaceholder")}
                                    />
                                </FormGroup>
                            }
                        </MuiContainer>

                    </CardContent>

                </Card>

            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.load();
    }

    ///
    public componentWillUnmount() {
        window.onbeforeunload = undefined;
    }

    ///
    public componentDidUpdate() {
        if (this.isRecordChanged()) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
    }

    ///
    render() {
        if (this.state.formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator type="waiting" />;
        }

        if (this.state.formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport error={this.state.lastError} />;
        }

        return (
            <Grid container justifyContent="space-around">
                <Grid item xs={12} md={10} lg={8}>

                    {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                    <RequestErrorDialog
                        open={this.state.formCycle == RequestLifeCycle.PostFailed}
                        error={this.state.lastError}
                        onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                    />

                    {this.state.dialog}

                    {this.renderToolbar()}

                    <form noValidate autoComplete="off" >
                        {this.renderFormMain()}
                        {this.renderRecordInfo()}
                    </form>

                    <Prompt
                        when={this.isRecordChanged()}
                        message={this.props.t("Common.Question.Unsave")}
                    />
                </Grid>
            </Grid>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(SetupUsersEdit)));